import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmtAUD = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : null;

function PriceCell({ amount, date, label, color, selected, onClick, disabled }) {
  const clickable = !!onClick && !disabled;
  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        border: `2px solid ${selected ? color : "transparent"}`,
        borderRadius: 1.5,
        px: 1.2, py: 0.6,
        background: selected ? `${color}12` : "transparent",
        minWidth: 90,
        transition: "all 0.12s",
        cursor: clickable ? "pointer" : disabled ? "not-allowed" : "default",
        opacity: disabled ? 0.4 : 1,
        userSelect: "none",
        "&:hover": clickable ? { border: `2px solid ${color}`, background: `${color}08` } : {},
      }}
    >
      <Typography fontSize="0.7rem" color="text.secondary" fontWeight={600} lineHeight={1.2}>{label}</Typography>
      <Typography fontSize="0.9rem" fontWeight={700} sx={{ color: selected ? color : "#374151" }}>
        {amount != null ? fmtAUD(amount) : <span style={{ color: "#9ca3af" }}>—</span>}
      </Typography>
      {date && <Typography fontSize="0.68rem" color="text.secondary">{date}</Typography>}
    </Box>
  );
}

export default function PricingReviewTab() {
  const [scripts, setScripts] = useState([]);
  const [prevPrices, setPrevPrices] = useState({});
  const [choices, setChoices] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    setSaved(false);
    Promise.all([
      phappsRequest(API_PATHS.ZEPHYR_PROCESSING_PRICING_REVIEW, "GET"),
      phappsRequest(API_PATHS.ZEPHYR_PROCESSING_PRICING_PREVIOUS, "GET"),
    ]).then(([reviewRes, prevRes]) => {
      const data = reviewRes?.data ?? [];
      setScripts(data);

      const prevMap = {};
      (prevRes?.data ?? []).forEach(p => { prevMap[p.scriptID] = p; });
      setPrevPrices(prevMap);

      const initial = {};
      data.forEach(s => {
        const prevAmt = prevMap[s.ScriptID]?.prevInvAmount;
        const calcAmt = Number(s.InvAmount);
        initial[s.ScriptID] = (prevAmt != null && Number(prevAmt) === calcAmt) ? "calculated" : "pharmacy";
      });
      setChoices(initial);
    })
    .catch(() => setError("Failed to load pricing review scripts."))
    .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setAll = (val) => setChoices(Object.fromEntries(scripts.map(s => [s.ScriptID, val])));

  const handleRecalculate = async () => {
    setRecalculating(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_RECALCULATE, "POST");
      load();
    } catch {
      setError("Recalculation failed.");
    } finally {
      setRecalculating(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = scripts.map(s => {
        const choice = choices[s.ScriptID] || "pharmacy";
        let invAmount;
        if (choice === "pharmacy")    invAmount = Number(s.PharmAmount);
        else if (choice === "previous") invAmount = Number(prevPrices[s.ScriptID]?.prevInvAmount ?? s.PharmAmount);
        else                           invAmount = Number(s.InvAmount);
        return { scriptID: s.ScriptID, invAmount };
      });
      await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_PRICING_SAVE, "POST", { choices: payload });
      setSaved(true);
      setScripts([]);
    } catch {
      setError("Failed to save pricing choices.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
      <CircularProgress sx={{ color: Z_PURPLE }} />
    </Box>
  );

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography fontWeight={700} fontSize="1rem" color={Z_PURPLE}>Pricing Review</Typography>
          {scripts.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {scripts.length} script{scripts.length !== 1 ? "s" : ""} with calculated price &gt;20% above pharmacy price
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={recalculating ? <CircularProgress size={14} /> : <RefreshOutlinedIcon />}
            disabled={recalculating || saving}
            onClick={handleRecalculate}
            sx={{ borderColor: Z_PURPLE, color: Z_PURPLE, fontWeight: 600 }}
          >
            {recalculating ? "Recalculating…" : "Recalculate"}
          </Button>
          {scripts.length > 0 && <>
            <Button size="small" variant="outlined" disabled={saving || recalculating}
              onClick={() => setAll("pharmacy")}
              sx={{ borderColor: Z_BLUE, color: Z_BLUE, fontWeight: 600 }}>
              All Pharmacy
            </Button>
            <Button size="small" variant="outlined" disabled={saving || recalculating}
              onClick={() => setAll("previous")}
              sx={{ borderColor: "#7c3aed", color: "#7c3aed", fontWeight: 600 }}>
              All Previous
            </Button>
            <Button size="small" variant="outlined" disabled={saving || recalculating}
              onClick={() => setAll("calculated")}
              sx={{ borderColor: "#16a34a", color: "#16a34a", fontWeight: 600 }}>
              All Calculated
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveOutlinedIcon />}
              disabled={saving || recalculating}
              onClick={handleSaveAll}
              sx={{ background: Z_PURPLE, "&:hover": { background: "#2a1240" }, fontWeight: 700 }}
            >
              {saving ? "Saving…" : "Save All"}
            </Button>
          </>}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Pricing choices saved — scripts moved to Matched.</Alert>}

      {!saved && scripts.length === 0 && (
        <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
          <Typography>No scripts awaiting pricing review.</Typography>
        </Box>
      )}

      {scripts.length > 0 && (
        <TableContainer component={Paper} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, boxShadow: "none" }}>
          <Table size="small">
            <TableHead sx={{ background: `${Z_PURPLE}08` }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1.2 }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>Patient</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>Chemist</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>Qty</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, minWidth: 280 }}>Prices</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scripts.map(s => {
                const selected = choices[s.ScriptID] || "pharmacy";
                const pharmAmt = Number(s.PharmAmount);
                const calcAmt = Number(s.InvAmount);
                const prev = prevPrices[s.ScriptID];
                const prevAmt = prev ? Number(prev.prevInvAmount) : null;
                const prevDate = prev ? fmtDate(prev.prevPayDate) : null;
                const pctDiff = pharmAmt > 0 ? Math.round(((calcAmt - pharmAmt) / pharmAmt) * 100) : 0;

                return (
                  <TableRow key={s.ScriptID} hover sx={{ "&:hover": { background: "#f8fafc" } }}>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography fontSize="0.82rem" fontWeight={600}>{s.TxtItem}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="0.82rem">{s.FirstName} {s.LastName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="0.82rem">{s.ChemistName || "—"}</Typography>
                    </TableCell>
                    <TableCell>{s.Qty}</TableCell>

                    {/* Three prices side by side — click to select */}
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
                        <PriceCell
                          label="Pharmacy"
                          amount={pharmAmt}
                          color={Z_BLUE}
                          selected={selected === "pharmacy"}
                          onClick={() => setChoices(prev => ({ ...prev, [s.ScriptID]: "pharmacy" }))}
                        />
                        <PriceCell
                          label={`Calculated +${pctDiff}%`}
                          amount={calcAmt}
                          color="#16a34a"
                          selected={selected === "calculated"}
                          onClick={() => setChoices(prev => ({ ...prev, [s.ScriptID]: "calculated" }))}
                        />
                        <PriceCell
                          label="Last Paid"
                          amount={prevAmt}
                          date={prevDate}
                          color="#7c3aed"
                          selected={selected === "previous"}
                          onClick={() => setChoices(prev => ({ ...prev, [s.ScriptID]: "previous" }))}
                          disabled={prevAmt == null}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
